import { getUser } from '@api';
import { ApiErrorsKey } from '@constants';
import { AuthUser } from '@domain/types';
import { ErrorPage } from '@launcher/components/ErrorPage';
import { useAuthenticateUser } from '@launcher/hooks/useAuthenticateUser.hook';
import { isDesktopApp, isUsingNewElectron } from '@launcher/utils';
import { tracking } from '@services/analytics';
import { Http, HttpError } from '@services/http';
import { selectDesktopIsLoadingSettings } from '@store/desktop/desktop.selector';
import { getAuthenticationError, getIsAuthenticated } from '@store/user/user.selector';
import { AuthenticationError } from '@store/user/user.state';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { DesktopLoginProvider } from './DesktopLogin.provider';

interface AuthError {
  error?: Error;
  errorKey?: AuthenticationError;
}

const parseErrorKey = async (response: Response): Promise<ApiErrorsKey | undefined> => {
  try {
    const body = await response.json();
    return body.key;
  } catch {
    return undefined;
  }
};

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const onAuthenticateUserSuccess = useAuthenticateUser();
  const isAuthenticated = useSelector(getIsAuthenticated);
  const reduxAuthError = useSelector(getAuthenticationError);
  const desktopIsLoadingSettings = useSelector(selectDesktopIsLoadingSettings);
  const [enabledDesktopLogin, setDesktopLoginEnabled] = useState(false);
  const [authError, setAuthError] = useState<AuthError>();

  const handleHttpError = (error: HttpError | Error) => {
    if (error instanceof HttpError) {
      if (error.statusCode === 501) {
        window.location.reload();
      } else if (error.key === ApiErrorsKey.USER_IDENTITY_NOT_FOUND) {
        setAuthError({ error, errorKey: AuthenticationError.MisconfiguredAccount });
      } else {
        setAuthError({ error, errorKey: AuthenticationError.FetchingUser });
      }
    } else {
      setAuthError({ error: error as Error, errorKey: AuthenticationError.Default });
    }
  };

  /*
  Get user from backend resonse and dispatch it to redux store
  */
  const onUserAuthenticated = useCallback(
    async (response: Response) => {
      const user = await response.json();

      // Set CSRF token if getting it from user api call
      const token = Http.getInstance().getCsrfTokenFromHeaders(response);
      if (token) {
        Http.getInstance().setCsrfToken(token);
      }

      // Dispatch user to redux store
      onAuthenticateUserSuccess(user as unknown as AuthUser);

      // Start app load tracking right after authentication success
      tracking.trackAppLoadStart();
    },
    [onAuthenticateUserSuccess],
  );

  /*
  Fetch loggued user from backend and dispatch it to redux store
  */
  const fetchUser = useCallback(async () => {
    try {
      // If user is switching workspace, we force re-login to this specific workspace
      const forcedWorkspaceId = localStorage.getItem('FORCE_LOGIN_TO_WORKSPACE_ID');
      if (forcedWorkspaceId) {
        setDesktopLoginEnabled(true);
        return;
      }

      const response = await getUser();

      if (response.status === 200) {
        await onUserAuthenticated(response);
        return;
      }

      // On web, we redirect to the login page
      // On desktop, activate the login process accross the webview
      if (response.status === 401) {
        if ((await isUsingNewElectron()) || !isDesktopApp()) {
          await Http.getInstance().redirectToLogin();
        } else {
          setDesktopLoginEnabled(true);
        }
      } else {
        throw new HttpError({
          message: 'Error when fetching user',
          statusCode: response.status,
          key: await parseErrorKey(response),
        });
      }
    } catch (error) {
      handleHttpError(error as Error | HttpError);
    }
  }, [onUserAuthenticated]);

  useEffect(() => {
    // Do nothing if desktop app is loading settings
    if (isDesktopApp() && desktopIsLoadingSettings) {
      return;
    }

    // Fetch user if not already authenticated
    if (!isAuthenticated) {
      fetchUser();
    }
  }, [desktopIsLoadingSettings, fetchUser, isAuthenticated]);

  if (authError?.errorKey || reduxAuthError) {
    return <ErrorPage error={authError?.error} errorKey={authError?.errorKey || reduxAuthError} />;
  }

  // User has been fetched but without success, render desktop login provider only on desktop
  if (isDesktopApp() && enabledDesktopLogin) {
    return <DesktopLoginProvider>{children}</DesktopLoginProvider>;
  }

  return <>{children}</>;
};
